<template>
	<div class="container-fluid viewport-shell">
		<div
			class="d-flex justify-content-between align-items-center pt-4 mb-4 flex-shrink-0"
		>
			<h2>
				<i class="bi bi-droplet-half me-2"></i>
				Pool Condition Review
			</h2>
			<div class="d-flex gap-2 align-items-center">
				<button
					class="btn btn-outline-primary btn-sm"
					:disabled="loading"
					@click="nextPool"
					title="Next pool (N)"
				>
					<i class="bi bi-arrow-repeat me-1"></i>
					Next pool
					<kbd class="key-chip">N</kbd>
				</button>
				<RouterLink
					to="/_/sudo/vision-tasks"
					class="btn btn-outline-secondary btn-sm"
				>
					<i class="bi bi-arrow-left me-1"></i>
					Back
				</RouterLink>
				<button
					class="btn btn-outline-secondary btn-sm"
					title="Full instructions: keyboard shortcuts (?)"
					@click="showShortcutsModal = true"
				>
					<i class="bi bi-question-circle me-1"></i>
					Instructions
					<kbd class="key-chip">?</kbd>
				</button>
			</div>
		</div>

		<div v-if="loading" class="text-center text-muted py-5">
			<div class="spinner-border text-primary mb-2" role="status"></div>
			<p class="small">Loading pool...</p>
		</div>

		<div v-else-if="error" class="alert alert-danger">{{ error }}</div>

		<div v-else-if="pool === null" class="text-center text-muted py-5">
			<p>All pools have received a condition verdict.</p>
			<button class="btn btn-outline-primary btn-sm" @click="refreshQueue">
				<i class="bi bi-arrow-repeat"></i> Refresh
			</button>
		</div>

		<div v-else-if="!pool.tile" class="alert alert-warning">
			Could not compute a centering tile for this pool.
		</div>

		<div v-else class="finding-layout">
			<div
				class="d-flex align-items-center gap-3 mb-2 text-muted flex-shrink-0"
			>
				<span>
					<RouterLink
						:to="`${BASE_PATH}/${pool.id}`"
						class="link-primary text-decoration-none"
						title="Link to this pool"
					>
						Pool #{{ pool.id }}
						<i class="bi bi-box-arrow-up-right"></i>
					</RouterLink>
				</span>
				<span
					class="badge"
					:style="{ background: conditionColor(pool.condition) }"
					title="Current stored condition — this pool awaits a verdict"
				>
					{{ pool.condition }}
				</span>
				<span v-if="pool.itemId" class="font-monospace" title="Planet item">
					Item {{ pool.itemId }}
				</span>
				<span>{{ formatDate(pool.createdAt) }}</span>
				<span v-if="pool.tile">
					Tile {{ pool.tile.z }}/{{ pool.tile.x }}/{{ pool.tile.y }}</span
				>
				<span class="small text-muted ms-auto">
					Geometry is fixed — this review only records the pool's condition.
				</span>
			</div>

			<!-- Verdict actions docked directly above the imagery, each shortcut
			     visible on its button. Selecting a condition records it (the pool
			     is marked reviewed) and auto-advances; Reject sends the pool back
			     to the earlier Correct Finding review (after a confirmation);
			     Skip advances without recording a verdict. -->
			<div
				class="d-flex align-items-center gap-2 flex-shrink-0 mb-2 toolbar-row"
			>
				<button
					v-for="v in verdicts"
					:key="v.value"
					class="btn btn-sm verdict-btn"
					:disabled="loading || saving || pool === null"
					:style="{ background: v.color, borderColor: v.color }"
					:title="`Record this pool as ${v.label} (${v.key})`"
					@click="submitCondition(v.value)"
				>
					{{ v.label }}
					<kbd class="key-chip light">{{ v.key }}</kbd>
				</button>
				<button
					class="btn btn-outline-danger btn-sm"
					:disabled="loading || saving || pool === null"
					title="The pool outline or imagery is wrong — send it back to Correct Finding (R)"
					@click="showRejectModal = true"
				>
					<i class="bi bi-arrow-counterclockwise me-1"></i>
					Reject
					<kbd class="key-chip">R</kbd>
				</button>
				<button
					class="btn btn-outline-secondary btn-sm"
					:disabled="loading || saving || pool === null"
					title="Skip this pool for now — no verdict is recorded (X)"
					@click="skipPool"
				>
					<i class="bi bi-skip-forward me-1"></i>
					Skip
					<kbd class="key-chip">X</kbd>
				</button>
				<button
					class="btn btn-outline-secondary btn-sm"
					:disabled="loading"
					@click="showOutline = !showOutline"
					title="Toggle the pool outline overlay (O)"
				>
					<i
						:class="(showOutline ? 'bi bi-eye-slash' : 'bi bi-eye') + ' me-1'"
					></i>
					{{ showOutline ? "Hide outline" : "Show outline" }}
					<kbd class="key-chip">O</kbd>
				</button>
				<div
					class="btn-group btn-group-sm ms-auto"
					role="group"
					aria-label="Pane zoom"
				>
					<button
						class="btn btn-outline-secondary"
						:disabled="loading || pool === null"
						title="Zoom panes out (−)"
						@click="zoomBy(1 / 1.25)"
					>
						<i class="bi bi-zoom-out"></i>
					</button>
					<button
						class="btn btn-outline-secondary"
						:disabled="loading || pool === null"
						title="Zoom panes in (+)"
						@click="zoomBy(1.25)"
					>
						<i class="bi bi-zoom-in"></i>
					</button>
					<button
						class="btn btn-outline-secondary"
						:disabled="loading || pool === null"
						title="Reset pane zoom (0)"
						@click="zoomReset()"
					>
						<i class="bi bi-arrows-fullscreen"></i>
					</button>
				</div>
			</div>

			<div
				ref="panelsArea"
				:key="pool.id"
				class="panels-area"
				:style="{
					gap: PANEL_GAP + 'px',
					gridTemplateColumns:
						layout.mode === 'grid3x2'
							? `repeat(3, ${layout.size}px)`
							: layout.mode === 'grid2x3'
								? `repeat(2, ${layout.size}px)`
								: undefined,
				}"
			>
				<div v-if="layout.mode === 'error'" class="text-center text-muted">
					<p class="mb-1">
						I'm sorry the display can not be viewed at this screen size
					</p>
					<p class="small mb-0">Try making the window larger.</p>
				</div>
				<template v-else>
					<div
						v-for="v in variants"
						:key="v.id"
						class="card border-0 shadow-sm"
						:style="{ width: layout.size + 'px' }"
					>
						<div class="card-header py-2">
							<strong>{{ v.label }}</strong>
						</div>
						<div
							class="tile-panel"
							:style="{
								width: layout.size + 'px',
								height: layout.size + 'px',
								aspectRatio: 'auto',
							}"
						>
							<div class="tile-zoom" :style="zoomStyle(v.id)">
								<img
									:src="tileURL(v.id)"
									alt=""
									class="tile-img"
									loading="lazy"
									@error="onImgError($event, v.id)"
								/>
								<svg
									v-if="showOutline && paneViews[v.id]?.hasOutline"
									class="tile-overlay"
									viewBox="0 0 256 256"
									preserveAspectRatio="none"
								>
									<!-- The pool under review — a fixed outline over the
									     imagery; geometry is read-only in this task. -->
									<polygon
										:points="paneViews[v.id]?.points"
										fill="none"
										:stroke="POOL_OUTLINE_COLOR"
										:stroke-width="outlineStrokeWidth(v.id)"
										vector-effect="non-scaling-stroke"
									/>
								</svg>
							</div>
						</div>
					</div>
				</template>
			</div>
		</div>
	</div>

	<ToastNotification
		:show="toastShow"
		:title="toastTitle"
		:message="toastMessage"
	/>

	<Teleport to="body">
		<!-- Reject confirmation: reject kicks the pool back to the earlier
		     Correct Finding review, so it needs an explicit confirm. -->
		<div v-if="showRejectModal">
			<div class="modal-backdrop fade show"></div>
			<div
				class="modal fade show d-block"
				tabindex="-1"
				@click.self="showRejectModal = false"
			>
				<div class="modal-dialog modal-dialog-centered">
					<div class="modal-content">
						<div class="modal-header">
							<h5 class="modal-title">
								<i class="bi bi-arrow-counterclockwise text-danger me-1"></i>
								Reject pool #{{ pool?.id }}?
							</h5>
							<button
								type="button"
								class="btn-close"
								@click="showRejectModal = false"
							></button>
						</div>
						<div class="modal-body">
							<p>
								The pool is sent back to the
								<strong>Correct Finding</strong> review, where its geometry or
								verdict can be fixed. It will return to this queue once that
								review is resolved.
							</p>
							<p class="mb-0 small text-muted">
								No condition is recorded for this pool.
							</p>
						</div>
						<div class="modal-footer">
							<button
								type="button"
								class="btn btn-outline-secondary"
								:disabled="saving"
								@click="showRejectModal = false"
							>
								Cancel
							</button>
							<button
								type="button"
								class="btn btn-danger"
								:disabled="saving"
								@click="rejectPool"
							>
								<i class="bi bi-arrow-counterclockwise me-1"></i>
								Send back to Correct Finding
							</button>
						</div>
					</div>
				</div>
			</div>
		</div>

		<div v-if="showShortcutsModal">
			<div class="modal-backdrop fade show"></div>
			<div
				class="modal fade show d-block"
				tabindex="-1"
				@click.self="showShortcutsModal = false"
			>
				<div class="modal-dialog modal-dialog-centered">
					<div class="modal-content">
						<div class="modal-header">
							<h5 class="modal-title">
								<i class="bi bi-question-circle"></i>
								Instructions
							</h5>
							<button
								type="button"
								class="btn-close"
								@click="showShortcutsModal = false"
							></button>
						</div>
						<div class="modal-body">
							<h6 class="text-muted text-uppercase small fw-bold mb-2">Task</h6>
							<p class="small">
								Review one pool at a time across six satellite-imagery panes
								(all Planet data — RGB plus water-sensitive band renders). Judge
								the pool's water condition and record it with a verdict button
								or its shortcut; the review auto-advances to the next pool. The
								pool outline is fixed — geometry is resolved in earlier reviews.
							</p>
							<h6 class="text-muted text-uppercase small fw-bold mb-2">
								Colors
							</h6>
							<table class="table table-sm small mb-3">
								<tbody>
									<tr>
										<td>
											<span
												class="d-inline-block me-1"
												style="
													width: 0.9rem;
													height: 0.9rem;
													border-radius: 0.25rem;
													background: #0b52d6;
													vertical-align: middle;
												"
											></span>
										</td>
										<td>Maintained — clean, clear water</td>
									</tr>
									<tr>
										<td>
											<span
												class="d-inline-block me-1"
												style="
													width: 0.9rem;
													height: 0.9rem;
													border-radius: 0.25rem;
													background: #22c55e;
													vertical-align: middle;
												"
											></span>
										</td>
										<td>Unmaintained — algae / green water</td>
									</tr>
									<tr>
										<td>
											<span
												class="d-inline-block me-1"
												style="
													width: 0.9rem;
													height: 0.9rem;
													border-radius: 0.25rem;
													background: #a855f7;
													vertical-align: middle;
												"
											></span>
										</td>
										<td>Partial — partly filled / partly covered</td>
									</tr>
									<tr>
										<td>
											<span
												class="d-inline-block me-1"
												style="
													width: 0.9rem;
													height: 0.9rem;
													border-radius: 0.25rem;
													background: #ef4444;
													vertical-align: middle;
												"
											></span>
										</td>
										<td>Dry — no water</td>
									</tr>
									<tr>
										<td>
											<span
												class="d-inline-block me-1"
												style="
													width: 0.9rem;
													height: 0.9rem;
													border-radius: 0.25rem;
													background: #ffc107;
													vertical-align: middle;
												"
											></span>
										</td>
										<td>The pool outline (fixed)</td>
									</tr>
								</tbody>
							</table>
							<h6 class="text-muted text-uppercase small fw-bold mb-2">
								Keyboard
							</h6>
							<table class="table table-sm small mb-0">
								<tbody>
									<tr>
										<td><kbd>1</kbd></td>
										<td>Maintained</td>
									</tr>
									<tr>
										<td><kbd>2</kbd></td>
										<td>Unmaintained</td>
									</tr>
									<tr>
										<td><kbd>3</kbd></td>
										<td>Partial</td>
									</tr>
									<tr>
										<td><kbd>4</kbd></td>
										<td>Dry</td>
									</tr>
									<tr>
										<td><kbd>R</kbd></td>
										<td>
											Reject — send the pool back to Correct Finding (opens a
											confirmation)
										</td>
									</tr>
									<tr>
										<td><kbd>X</kbd></td>
										<td>Skip — advance without recording a verdict</td>
									</tr>
									<tr>
										<td><kbd>N</kbd></td>
										<td>Next pool</td>
									</tr>
									<tr>
										<td><kbd>O</kbd></td>
										<td>Toggle the pool outline overlay</td>
									</tr>
									<tr>
										<td><kbd>+</kbd> / <kbd>&#8722;</kbd></td>
										<td>Zoom all panes in / out</td>
									</tr>
									<tr>
										<td><kbd>0</kbd></td>
										<td>Reset pane zoom</td>
									</tr>
									<tr>
										<td><kbd>?</kbd></td>
										<td>Show this instructions dialog</td>
									</tr>
									<tr>
										<td><kbd>Esc</kbd></td>
										<td>Close this dialog / cancel</td>
									</tr>
								</tbody>
							</table>
						</div>
					</div>
				</div>
			</div>
		</div>
	</Teleport>
</template>

<style scoped>
/* Viewport-locked shell (same pattern as CorrectFinding): the page fills the
   viewport so the six variant panels are always visible without scrolling. */
.viewport-shell {
	height: 100vh;
	height: 100dvh;
	display: flex;
	flex-direction: column;
}

/* Pool content fills the viewport below the toolbar; the meta row stays
   fixed and the panels area absorbs the remaining space (flex-basis: 0 so
   the area's height never feeds back into the panel size it drives). */
.finding-layout {
	flex: 1 1 0;
	min-height: 0;
	display: flex;
	flex-direction: column;
}

/* Panels area: lays the six variant cards out from the upper-left in a fixed
   3x2 (or, on very short windows, 2x3) grid, never scrolls. The column count
   and the inline `gap` are driven by the layout math so what is rendered
   always matches the computed mode. In error mode the single message div
   sits top-left on one column. */
.panels-area {
	flex: 1 1 0;
	min-height: 0;
	overflow: hidden;
	display: grid;
	justify-content: start;
	align-content: start;
	align-items: start;
}

.tile-panel {
	position: relative;
	width: 100%;
	aspect-ratio: 1 / 1;
	background: #e9ecef;
	overflow: hidden;
}
.tile-img {
	position: absolute;
	inset: 0;
	width: 100%;
	height: 100%;
	object-fit: fill;
}
.tile-overlay {
	position: absolute;
	inset: 0;
	width: 100%;
	height: 100%;
}
/* Zoom wrapper: scales/translates the tile imagery (img + SVG overlay) so the
   pool fills 63% of the panel, centered. The transform is applied about the
   panel's top-left (origin 0 0); pool outlines are projected in the same
   256-space so they stay aligned at every zoom. */
.tile-zoom {
	position: absolute;
	inset: 0;
	transform-origin: 0 0;
	will-change: transform;
}

/* Visible shortcut keys (toolbar buttons + legend): a light chip that
   inherits the surrounding color, so it reads cleanly on every button
   variant and on the muted legend. .light sits on the filled condition
   buttons (dark text is unreadable there). */
kbd.key-chip {
	font-family: var(--bs-font-monospace);
	font-size: 0.8em;
	font-weight: 600;
	line-height: 1;
	vertical-align: middle;
	color: inherit;
	background: rgba(33, 37, 41, 0.08);
	border: 1px solid rgba(33, 37, 41, 0.25);
	border-bottom-width: 2px;
	border-radius: 0.3rem;
	padding: 0.12rem 0.32rem;
	box-shadow: none;
}
kbd.key-chip.light {
	color: #fff;
	background: rgba(255, 255, 255, 0.2);
	border-color: rgba(255, 255, 255, 0.55);
}
.toolbar-row .btn {
	white-space: nowrap;
}
.btn .key-chip {
	margin-left: 0.35rem;
}
.verdict-btn {
	color: #fff;
}
.verdict-btn:disabled {
	opacity: 0.5;
}
</style>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { RouterLink, useRoute, useRouter } from "vue-router";
import { apiClient } from "@/client";
import ToastNotification from "@/components/ToastNotification.vue";
import type { VisionConditionReviewPool } from "@/type/api";

interface ImageryVariant {
	id: string;
	label: string;
}

// Panes: five Planet-data views plus a Stadia satellite reference pane
// (operator: no pixel-variation pane). Planet panes use the backend's
// pool-centered offset tile; the Stadia pane uses the plain slippy tile that
// contains the pool.
const variants: ImageryVariant[] = [
	{ id: "planet", label: "Planet RGB" },
	{ id: "blue-excess", label: "Blue Excess" },
	{ id: "blue-v-red", label: "Red vs Blue" },
	{ id: "ndwi", label: "NDWI" },
	{ id: "nir", label: "NIR" },
	{ id: "stadia", label: "Stadia" },
];

const TILE_PX = 256;
const BASE_PATH = "/_/sudo/vision-tasks/pool-condition";
// The pool under review — fixed outline color (amber, matching the sibling
// per-pool review). Condition colors are not used for geometry here: the
// review records a condition, it does not display one.
const POOL_OUTLINE_COLOR = "#ffc107";

// Verdict buttons: operator-facing labels mapped onto the existing pool
// condition enum values (stored unchanged), colored with the CONDITION_COLORS
// palette used across the review views.
interface ConditionVerdict {
	value: string;
	label: string;
	key: string;
	color: string;
}
const verdicts: ConditionVerdict[] = [
	{ value: "blue", label: "Maintained", key: "1", color: "#0b52d6" },
	{ value: "green", label: "Unmaintained", key: "2", color: "#22c55e" },
	{ value: "partial", label: "Partial", key: "3", color: "#a855f7" },
	{ value: "dry", label: "Dry", key: "4", color: "#ef4444" },
];

// Standard review CONDITION_COLORS (unknown/blue/green/dry/partial/covered
// plus the rarer murky/false-pool values), used for the meta-row badge.
const CONDITION_COLORS: Record<string, string> = {
	unknown: "#facc15",
	blue: "#0b52d6",
	green: "#22c55e",
	dry: "#ef4444",
	partial: "#a855f7",
	covered: "#f97316",
	murky: "#78716c",
	"false pool": "#6b7280",
};

// Adaptive six-panel layout thresholds, mirroring Correct Finding's panel
// math (sizes are chosen so the 3x2 grid fits the panels area; a 2x3 grid is
// the fallback for very short windows).
const MIN_PANEL = 220;
const MAX_PANEL = 520;
const PANEL_GAP = 12;
const CARD_HEADER_H = 41;

type PanelLayout = { mode: "grid3x2" | "grid2x3" | "error"; size: number };
const layout = ref<PanelLayout>({ mode: "grid3x2", size: MIN_PANEL });
const panelsArea = ref<HTMLElement | null>(null);

const route = useRoute();
const router = useRouter();

const loading = ref(false);
const saving = ref(false);
const error = ref<string | null>(null);
const pool = ref<VisionConditionReviewPool | null>(null);
const brokenVariants = ref<Set<string>>(new Set());
const showOutline = ref(true);
const showRejectModal = ref(false);
const showShortcutsModal = ref(false);
const toastShow = ref(false);
const toastTitle = ref("");
const toastMessage = ref("");

// Per-session queue cursor: the id of the last pool served to this browser.
// Queue loads pass ?after=<cursor> so an advance (verdict / skip / reject)
// moves past the pool just shown; the cursor resets on a fresh load so pools
// skipped earlier (condition still 'unknown') surface again later — skip
// records nothing, per the review lifecycle.
const queueCursor = ref<number | null>(null);

// The id of the pool currently shown, so the route watcher can distinguish a
// URL change we made ourselves (after a next-load) from a real navigation.
const loadedPoolId = ref<string | null>(null);

function showToast(title: string, message: string) {
	toastTitle.value = title;
	toastMessage.value = message;
	toastShow.value = true;
	setTimeout(() => {
		toastShow.value = false;
	}, 5000);
}

function conditionColor(condition: string): string {
	return CONDITION_COLORS[condition] ?? CONDITION_COLORS["unknown"];
}

function formatDate(iso: string): string {
	if (!iso) return "\u2014";
	return new Date(iso).toLocaleDateString("en-US", {
		year: "numeric",
		month: "short",
		day: "numeric",
	});
}

function tileURL(variantId: string): string {
	const view = paneViews.value[variantId];
	const t = view?.tile;
	if (!t) return "";
	if (variantId === "stadia") {
		return `/api/tile/stadia/alidade_satellite/${t.z}/${t.x}/${t.y}`;
	}
	const baseURLs: Record<string, string> = {
		planet: "/api/tile/planet/any/{z}/{x}/{y}?bands=3,2,1",
		"blue-excess": "/api/tile/planet/any/{z}/{x}/{y}?transform=blue-excess",
		"blue-v-red": "/api/tile/planet/any/{z}/{x}/{y}?transform=blue-v-red",
		ndwi: "/api/tile/planet/any/{z}/{x}/{y}?transform=ndwi",
		nir: "/api/tile/planet/any/{z}/{x}/{y}?band=4&brightness=auto",
	};
	let url = baseURLs[variantId]
		.replace("{z}", String(t.z))
		.replace("{x}", String(t.x))
		.replace("{y}", String(t.y));
	if (t.offset) {
		url += "&offset";
	}
	if (pool.value?.itemId) {
		url += `&source=${encodeURIComponent(pool.value.itemId)}`;
	}
	return url;
}

function collectRings(geom: {
	type: string;
	coordinates: unknown;
}): [number, number][][] {
	const coords = geom.coordinates as unknown;
	if (geom.type === "Polygon") {
		return (coords as [number, number][][]) ?? [];
	}
	if (geom.type === "MultiPolygon") {
		const polys = coords as [number, number][][][];
		if (!polys || polys.length === 0) return [];
		return polys[0] ?? [];
	}
	return [];
}

function tileXFrac(lng: number, z: number): number {
	return ((lng + 180) / 360) * Math.pow(2, z);
}

function tileYFrac(lat: number, z: number): number {
	const latRad = (lat * Math.PI) / 180;
	return (
		((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) *
		Math.pow(2, z)
	);
}

// Project a geographic point into tile pixel space (0..256) using the same
// Web-Mercator math as Correct Finding. An offset tile centers on the corner
// at fractional (x+1, y+1) — the corner sits at the 256px image center — so
// the projection shifts and re-bases accordingly.
function projectToTile(
	lng: number,
	lat: number,
	tile: { z: number; x: number; y: number; offset?: boolean },
): { x: number; y: number } {
	const x = tileXFrac(lng, tile.z);
	const y = tileYFrac(lat, tile.z);
	if (tile.offset) {
		return {
			x: (x - (tile.x + 1)) * TILE_PX + TILE_PX / 2,
			y: (y - (tile.y + 1)) * TILE_PX + TILE_PX / 2,
		};
	}
	return { x: (x - tile.x) * TILE_PX, y: (y - tile.y) * TILE_PX };
}

// Project the pool's outer ring into tile-pixel points (0..256), dropping a
// repeated closing vertex. Returns null when the geometry has no ring.
function geometryToTilePoints(
	geom: { type: string; coordinates: unknown } | null | undefined,
	t: { z: number; x: number; y: number; offset?: boolean },
): { x: number; y: number }[] | null {
	if (!geom) return null;
	const rings = collectRings(geom);
	if (rings.length === 0) return null;
	let pts = rings[0].map(([lng, lat]) => projectToTile(lng, lat, t));
	if (pts.length > 1) {
		const first = pts[0];
		const last = pts[pts.length - 1];
		if (Math.abs(last.x - first.x) < 0.5 && Math.abs(last.y - first.y) < 0.5) {
			pts = pts.slice(0, -1);
		}
	}
	return pts;
}

// Per-pane tile + zoom machinery.
//
// Planet panes show the backend's pool-centered offset tile (the pool sits at
// the 256px image center), so they project through pool.tile. The Stadia pane
// is a plain slippy tile that contains the pool, so it needs its own tile and
// projection. Each pane therefore computes its outline and framing in its own
// tile space; all six panes share the zoom multiplier.

// The pool's geometry centroid, used to pick the Stadia pane's tile.
function centroidOf(
	geom: { type: string; coordinates: unknown } | null | undefined,
): { lng: number; lat: number } | null {
	if (!geom) return null;
	const rings = collectRings(geom);
	let lng = 0;
	let lat = 0;
	let n = 0;
	for (const ring of rings) {
		for (const [a, b] of ring) {
			lng += a;
			lat += b;
			n++;
		}
	}
	if (n === 0) return null;
	return { lng: lng / n, lat: lat / n };
}

// The non-offset slippy tile (same zoom as the pool's planet tile) containing
// the pool's centroid — the Stadia pane's tile.
const stadiaTile = computed(() => {
	const t = pool.value?.tile;
	const p = pool.value;
	if (!t || !p) return null;
	const c = centroidOf(
		p.geometry as { type: string; coordinates: unknown } | null,
	);
	if (!c) return null;
	return {
		z: t.z,
		x: Math.floor(tileXFrac(c.lng, t.z)),
		y: Math.floor(tileYFrac(c.lat, t.z)),
	};
});

// Pane zoom multiplier on top of the auto-fit (pool fills 63% of the frame).
// Starts slightly zoomed out (0.8) so each pane shows a little context around
// the pool; the toolbar +/- buttons and the + - 0 keys adjust it, and all six
// panes share the same zoom.
const ZOOM_RESET = 0.8;
const ZOOM_MIN = 0.5;
const ZOOM_MAX = 6;
const zoomMul = ref(ZOOM_RESET);

function zoomBy(factor: number) {
	zoomMul.value = Math.min(
		ZOOM_MAX,
		Math.max(ZOOM_MIN, zoomMul.value * factor),
	);
}

function zoomReset() {
	zoomMul.value = ZOOM_RESET;
}

// Fit a pool's tile-space points so the pool is centered and its larger
// dimension fills 63% of the frame (never zooming below the tile), scaled by
// the shared zoom multiplier.
function fitFrame(
	pts: { x: number; y: number }[],
	mul: number,
): { s: number; tx: number; ty: number } {
	let minX = Infinity;
	let minY = Infinity;
	let maxX = -Infinity;
	let maxY = -Infinity;
	for (const p of pts) {
		if (p.x < minX) minX = p.x;
		if (p.x > maxX) maxX = p.x;
		if (p.y < minY) minY = p.y;
		if (p.y > maxY) maxY = p.y;
	}
	if (!isFinite(minX)) return { s: 1, tx: 0, ty: 0 };
	const boxW = maxX - minX;
	const boxH = maxY - minY;
	const boxCx = (minX + maxX) / 2;
	const boxCy = (minY + maxY) / 2;
	const autoS = (0.63 * TILE_PX) / Math.max(boxW, boxH, 0.001);
	const s = Math.max(1, autoS) * mul;
	return { s, tx: TILE_PX / 2 - boxCx * s, ty: TILE_PX / 2 - boxCy * s };
}

type TileLike = { z: number; x: number; y: number; offset?: boolean };

interface PaneView {
	tile: TileLike | null;
	points: string;
	hasOutline: boolean;
	frame: { s: number; tx: number; ty: number };
}

// Per-variant tile/outline/frame: the pool outline and zoom framing in each
// pane's own tile space (offset planet tile for planet panes, plain slippy
// tile for Stadia). Reacts to pool changes and the shared zoom multiplier.
const paneViews = computed<Record<string, PaneView>>(() => {
	const p = pool.value;
	const mul = zoomMul.value;
	const out: Record<string, PaneView> = {};
	for (const v of variants) {
		const tile: TileLike | null =
			v.id === "stadia" ? stadiaTile.value : (p?.tile ?? null);
		if (!p || !tile) {
			out[v.id] = {
				tile,
				points: "",
				hasOutline: false,
				frame: { s: 1, tx: 0, ty: 0 },
			};
			continue;
		}
		const geom = p.geometry as { type: string; coordinates: unknown } | null;
		const pts = geometryToTilePoints(geom, tile) ?? [];
		out[v.id] = {
			tile,
			points: pts.map((q) => `${q.x.toFixed(1)},${q.y.toFixed(1)}`).join(" "),
			hasOutline: pts.length > 0,
			frame: fitFrame(pts, mul),
		};
	}
	return out;
});

// The SVG polygon stroke is painted through the zoom wrapper's CSS scale:
// vector-effect cancels the viewBox stretch but NOT ancestor CSS transforms,
// so the stroke-width must be the inverse of the zoom to render a constant
// 2px on screen at every zoom level.
function outlineStrokeWidth(id: string): string {
	const frame = paneViews.value[id]?.frame;
	return String((2 / (frame?.s ?? 1)).toFixed(4));
}

function zoomStyle(id: string): Record<string, string> {
	const frame = paneViews.value[id]?.frame;
	if (!frame) return { transform: "none", transformOrigin: "0 0" };
	const k = layout.value.size / TILE_PX;
	return {
		transform: `translate(${frame.tx * k}px, ${frame.ty * k}px) scale(${frame.s})`,
		transformOrigin: "0 0",
	};
}

function onImgError(e: Event, variantId: string) {
	const target = e.target as HTMLImageElement;
	target.style.visibility = "hidden";
	brokenVariants.value.add(variantId);
}

async function loadPool(forceNext = false) {
	loading.value = true;
	error.value = null;
	brokenVariants.value = new Set();
	try {
		const poolId = forceNext
			? undefined
			: (route.params.poolId as string | undefined);
		let url = poolId
			? `/api/vision/review/condition-pool/${encodeURIComponent(poolId)}`
			: "/api/vision/review/condition-pool";
		if (!poolId && queueCursor.value != null) {
			url += `?after=${queueCursor.value}`;
		}
		const result = await apiClient.JSONGet<VisionConditionReviewPool | null>(
			url,
		);
		pool.value = result;
		if (result) {
			loadedPoolId.value = String(result.id);
			queueCursor.value = result.id;
			// A queue load has no poolId in the URL yet — write it so the pool
			// is shareable/bookmarkable.
			if (!poolId) {
				router.replace(`${BASE_PATH}/${result.id}`);
			}
		} else {
			loadedPoolId.value = null;
		}
	} catch (e: unknown) {
		const err = e as {
			response?: { data?: { message?: string } };
			message?: string;
		};
		error.value =
			err?.response?.data?.message || err?.message || "Failed to load pool.";
		pool.value = null;
		loadedPoolId.value = null;
	} finally {
		loading.value = false;
	}
}

function nextPool() {
	// Load the next queued pool regardless of any poolId in the URL. The
	// server advances past the current pool via the session cursor, so a
	// skipped pool is not immediately re-served.
	loadPool(true);
}

// A fresh queue start (initial load or the done-state Refresh): reset the
// cursor so pools skipped earlier in this session (still condition
// 'unknown') surface again — skip records nothing.
function refreshQueue() {
	queueCursor.value = null;
	loadedPoolId.value = null;
	loadPool(true);
}

async function submitCondition(value: string) {
	const p = pool.value;
	if (!p || saving.value || loading.value) return;
	saving.value = true;
	try {
		// The verdict is the existing pool update path: condition + reviewer /
		// reviewed-at are recorded, so the pool leaves the condition queue.
		await apiClient.JSONPut(`/api/vision/train/pool/${p.id}`, {
			condition: value,
		});
		const v = verdicts.find((x) => x.value === value);
		showToast(
			"Condition recorded",
			`Pool #${p.id} marked ${v?.label ?? value}.`,
		);
		await nextPool();
	} catch (e: any) {
		showToast(
			"Save failed",
			e?.response?.data?.message || e?.message || "Failed to record condition.",
		);
	} finally {
		saving.value = false;
	}
}

// Skip leaves the pool untouched (condition stays 'unknown', no review
// state) and advances; the pool reappears once the queue is exhausted or on
// a fresh load.
function skipPool() {
	const p = pool.value;
	if (!p || loading.value || saving.value) return;
	showToast(
		"Pool skipped",
		`Pool #${p.id} left unjudged — it will come around again until it receives a condition.`,
	);
	nextPool();
}

async function rejectPool() {
	const p = pool.value;
	if (!p || saving.value || loading.value) return;
	showRejectModal.value = false;
	saving.value = true;
	try {
		await apiClient.JSONPost(
			`/api/vision/review/condition-pool/${p.id}/reject`,
			{},
		);
		showToast(
			"Pool rejected",
			`Pool #${p.id} sent back to Correct Finding for geometry review.`,
		);
		await nextPool();
	} catch (e: any) {
		showToast(
			"Reject failed",
			e?.response?.data?.message || e?.message || "Failed to reject pool.",
		);
	} finally {
		saving.value = false;
	}
}

function onKeydown(e: KeyboardEvent) {
	// Ignore when focus is in a text input / textarea.
	const target = e.target as HTMLElement | null;
	if (
		target &&
		(target.tagName === "INPUT" ||
			target.tagName === "TEXTAREA" ||
			target.isContentEditable)
	) {
		return;
	}
	// Reserve browser/modifier shortcuts (e.g. Cmd+R) for their normal use.
	if (e.ctrlKey || e.metaKey || e.altKey) return;

	// While the reject confirmation is open, Enter confirms, Escape cancels.
	if (showRejectModal.value) {
		if (e.key === "Enter") void rejectPool();
		else if (e.key === "Escape") showRejectModal.value = false;
		return;
	}
	if (showShortcutsModal.value) {
		if (e.key === "Escape") showShortcutsModal.value = false;
		return;
	}

	switch (e.key.toLowerCase()) {
		case "1":
			if (!loading.value && !saving.value && pool.value)
				void submitCondition("blue");
			break;
		case "2":
			if (!loading.value && !saving.value && pool.value)
				void submitCondition("green");
			break;
		case "3":
			if (!loading.value && !saving.value && pool.value)
				void submitCondition("partial");
			break;
		case "4":
			if (!loading.value && !saving.value && pool.value)
				void submitCondition("dry");
			break;
		case "r":
			if (!loading.value && !saving.value && pool.value)
				showRejectModal.value = true;
			break;
		case "x":
			if (!loading.value && !saving.value && pool.value) skipPool();
			break;
		case "n":
			if (!loading.value) nextPool();
			break;
		case "o":
			showOutline.value = !showOutline.value;
			break;
		case "+":
		case "=":
			if (pool.value) zoomBy(1.25);
			break;
		case "-":
		case "_":
			if (pool.value) zoomBy(1 / 1.25);
			break;
		case "0":
			if (pool.value) zoomReset();
			break;
		case "?":
			e.preventDefault();
			showShortcutsModal.value = true;
			break;
		case "escape":
			if (showShortcutsModal.value) showShortcutsModal.value = false;
			else if (showRejectModal.value) showRejectModal.value = false;
			break;
	}
}

watch(
	() => route.params.poolId,
	(newId) => {
		const pid = (newId as string | undefined) ?? null;
		// Ignore the URL write we just made after a queue load.
		if (pid === loadedPoolId.value) return;
		loadPool();
	},
);

// The card-header strip height: the panel cards are the flex items, so each
// flex line consumes size + header, not just size. Measured from the DOM once
// the cards render; the constant seeds the first pass.
function headerHeight(): number {
	const el = panelsArea.value?.querySelector(".card-header");
	if (!el) return CARD_HEADER_H;
	return Math.ceil(el.getBoundingClientRect().height);
}

function clampPanelSize(s: number): number {
	return Math.min(MAX_PANEL, Math.max(MIN_PANEL, s));
}

// Choose the best arrangement that fits the panels area (W/H = its client
// box): a 3x2 grid is preferred (three panes per row, two rows); a 2x3 grid
// wins only on extremely short windows. When neither fits at a usable (>=
// MIN_PANEL) size, show the error state instead of squinty panels.
function recomputeLayout() {
	const el = panelsArea.value;
	if (!el) return;
	const w = el.clientWidth;
	const h = el.clientHeight;
	const hdr = headerHeight();
	// 3x2: two rows, each consuming size + card-header; two gaps per axis.
	const s3 = Math.min((w - 2 * PANEL_GAP) / 3, (h - 2 * hdr - PANEL_GAP) / 2);
	// 2x3: three rows of two.
	const s2 = Math.min((w - PANEL_GAP) / 2, (h - 3 * hdr - 2 * PANEL_GAP) / 3);
	if (s3 >= MIN_PANEL) {
		layout.value = { mode: "grid3x2", size: clampPanelSize(s3) };
	} else if (s2 >= MIN_PANEL) {
		layout.value = { mode: "grid2x3", size: clampPanelSize(s2) };
	} else {
		layout.value = { mode: "error", size: 0 };
	}
}

let panelsObserver: ResizeObserver | null = null;

watch(
	panelsArea,
	(el, oldEl) => {
		if (oldEl && panelsObserver) panelsObserver.unobserve(oldEl);
		if (el) {
			if (!panelsObserver) panelsObserver = new ResizeObserver(recomputeLayout);
			panelsObserver.observe(el);
			recomputeLayout();
			requestAnimationFrame(recomputeLayout);
		}
	},
	{ flush: "post" },
);

onMounted(() => {
	window.addEventListener("keydown", onKeydown);
	loadPool();
});

onUnmounted(() => {
	window.removeEventListener("keydown", onKeydown);
	panelsObserver?.disconnect();
	panelsObserver = null;
});
</script>
